import { fetchWithAuth } from "../../../api/http";
import { API } from "../../../api/api";
import { getAuthHeaders } from "../../../api/http";
import type { ScenariosBlock } from "../../impact/domain/scenarios";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PortfolioSummaryGroup {
  group_value: string | null;
  project_count: number;
  total_mw: number;
  dalys: number;
  pdfs: number;
  usd: number;
}

export interface PortfolioSummaryResponse {
  totals: PortfolioSummaryGroup;
  groups: PortfolioSummaryGroup[];
  group_by: string | null;
}

export interface ProjectWithImpacts {
  project_id: string;
  project_name: string;
  tech_type_id: string | null;
  nameplate_capacity: number;
  existing_project_flag: boolean;
  eia_ba_code: string | null;
  portfolio_name: string | null;
  status: string | null;
  latest_run_status: string | null;
  dalys: number | null;
  pdfs: number | null;
  usd: number | null;
  cod_date: string | null;
  capacity_factor: number | null;
  profile_source: string | null;
  hours_modeled: number | null;
  latitude: number | null;
  longitude: number | null;
  tags: Record<string, string[]>;
}

export interface TagOut {
  key: string;
  values: string[];
}

export interface TagDefinition {
  id: string;
  customer_id: string;
  tag_key: string;
  display_name: string | null;
  color: string | null;
  sort_order: number;
}

export interface PortfolioAggregate {
  project_count: number;
  impacts: {
    Total: { USD: number; DALYs: number; PDFs: number };
    Public_Health: { Impact_Category: string; Total_Impact: number; USD: number }[];
    Biodiversity: { Impact_Category: string; Total_Impact: number; USD: number }[];
    Economic: { Impact_Category: string; USD: number }[];
  };
  generation: Record<string, { Difference: number }>;
  emissions: Record<string, { Name: string; Value: number; Unit: string }[]>;
}

/**
 * Consolidated dashboard payload — totals + groups (former /summary) plus
 * both analysis-mode aggregates (former /aggregate) from a single backend
 * query. Lets the Ops/CtG toggle be client-side.
 */
export interface PortfolioOverviewResponse {
  totals: PortfolioSummaryGroup;
  groups: PortfolioSummaryGroup[];
  group_by: string | null;
  ctg: PortfolioAggregate;
  ops: PortfolioAggregate;
  /**
   * Fleet-level Central ⇄ Range bracket — each posture summed across the
   * scoped projects server-side (scenario-consistent, never per-project
   * min/max). Absent or status!=='complete' → central only.
   */
  scenarios?: ScenariosBlock;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

function buildFilterParams(filters?: Record<string, string>): string {
  if (!filters || Object.keys(filters).length === 0) return "";
  return Object.entries(filters)
    .map(([k, v]) => `filter[${encodeURIComponent(k)}]=${encodeURIComponent(v)}`)
    .join("&");
}

export function getPortfolioSummary(
  token: string,
  groupBy?: string,
  filters?: Record<string, string>,
): Promise<PortfolioSummaryResponse> {
  const parts: string[] = [];
  if (groupBy) parts.push(`group_by=${encodeURIComponent(groupBy)}`);
  const fp = buildFilterParams(filters);
  if (fp) parts.push(fp);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  return fetchWithAuth<PortfolioSummaryResponse>(
    `/portfolio/summary${qs}`,
    token,
  );
}

/**
 * Phase 4b portfolio aggregate — sum-of-rasters intensity heatmap
 * across the caller's customer portfolio. Returns null on 404 (no
 * aggregate built yet).
 */
export async function getPortfolioIntensityTiles(
  token: string,
  customerId?: string,
): Promise<import("../../model/domain/types").IntensityTileResponse | null> {
  const qs = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : "";
  try {
    return await fetchWithAuth<import("../../model/domain/types").IntensityTileResponse>(
      `/portfolio/airshed-intensity-tiles${qs}`,
      token,
    );
  } catch (err: unknown) {
    // fetchWithAuth throws on non-2xx; 404 is informative (no aggregate yet)
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("404") || msg.toLowerCase().includes("not built")) return null;
    throw err;
  }
}

export function getPortfolioProjects(
  token: string,
  opts?: { sort?: string; limit?: number; offset?: number; filters?: Record<string, string> },
): Promise<ProjectWithImpacts[]> {
  const params = new URLSearchParams();
  if (opts?.sort) params.set("sort", opts.sort);
  if (opts?.limit) params.set("limit", String(opts.limit));
  if (opts?.offset) params.set("offset", String(opts.offset));
  const fp = buildFilterParams(opts?.filters);
  let qs = params.toString();
  if (fp) qs = qs ? `${qs}&${fp}` : fp;
  return fetchWithAuth<ProjectWithImpacts[]>(
    `/portfolio/projects${qs ? `?${qs}` : ""}`,
    token,
  );
}

export async function downloadPortfolioExport(
  token: string,
  filters?: Record<string, string>,
): Promise<Blob> {
  const fp = buildFilterParams(filters);
  const qs = fp ? `?${fp}` : "";
  const url = `${API}/portfolio/export${qs}`;
  const response = await fetch(url, {
    headers: getAuthHeaders(token),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Export error: ${response.status}`);
  }
  return response.blob();
}

export function getPortfolioAggregate(
  token: string,
  filters?: Record<string, string>,
  analysis?: "ctg" | "ops",
): Promise<PortfolioAggregate> {
  const parts: string[] = [];
  if (analysis && analysis !== "ctg") parts.push(`analysis=${analysis}`);
  const fp = buildFilterParams(filters);
  if (fp) parts.push(fp);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  return fetchWithAuth<PortfolioAggregate>(`/portfolio/aggregate${qs}`, token);
}

export function getPortfolioOverview(
  token: string,
  groupBy?: string,
  filters?: Record<string, string>,
): Promise<PortfolioOverviewResponse> {
  const parts: string[] = [];
  if (groupBy) parts.push(`group_by=${encodeURIComponent(groupBy)}`);
  const fp = buildFilterParams(filters);
  if (fp) parts.push(fp);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  return fetchWithAuth<PortfolioOverviewResponse>(`/portfolio/overview${qs}`, token);
}

export function getTags(token: string): Promise<TagOut[]> {
  return fetchWithAuth<TagOut[]>("/portfolio/tags", token);
}

export function addTags(
  token: string,
  projectIds: string[],
  tags: { key: string; value: string }[],
): Promise<{ added: number }> {
  return fetchWithAuth<{ added: number }>("/portfolio/tags", token, {
    method: "POST",
    body: JSON.stringify({ project_ids: projectIds, tags }),
  });
}

export function removeTags(
  token: string,
  projectIds: string[],
  tags: { key: string; value: string }[],
): Promise<{ removed: number }> {
  return fetchWithAuth<{ removed: number }>("/portfolio/tags", token, {
    method: "DELETE",
    body: JSON.stringify({ project_ids: projectIds, tags }),
  });
}

export function getProjectTags(
  token: string,
  projectId: string,
): Promise<Record<string, string[]>> {
  return fetchWithAuth<Record<string, string[]>>(
    `/portfolio/projects/${projectId}/tags`,
    token,
  );
}

export function getTagDefinitions(token: string): Promise<TagDefinition[]> {
  return fetchWithAuth<TagDefinition[]>("/portfolio/tag-definitions", token);
}

export function createTagDefinition(
  token: string,
  body: { tag_key: string; display_name?: string; color?: string; sort_order?: number },
): Promise<TagDefinition> {
  return fetchWithAuth<TagDefinition>("/portfolio/tag-definitions", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
